// The Briefing's ATTACHMENTS entry, as state (busy-my-web#277).
//
// Two fetches, both from the BFF, both scoped by the same `scope` grammar every other bounded
// question uses (busy-main-bff#20):
//
//   documents/briefing?scope=…      how many of each class → the row's count and its chips
//   documents?class=&scope=…        the files themselves    → the list under the chips
//
// The counts are FETCHED rather than counted here for the reason the email briefing's summary is:
// the client does not hold the answer. Only the server knows which threads a story covers, and only
// busy-document-srv knows what each file IS — "invoice" and "receipt" are words inside the document,
// read once when its text was extracted. Guessing either on this side would put a stranger's quote
// in the user's Receipts, so the pure module (lib/attachmentsBriefing.js) never does.
//
// Everything is keyed by SCOPE. Home's attachments and a story's attachments are two different
// answers to the same question, and a story briefing that reused home's counts would tell the user
// their story had files it does not have — the exact class of bug the caching here exists to make
// impossible.

import api from '@/lib/services/general.service'
import { nextAttachmentClass, parseAttachmentClass } from '@/lib/attachmentsBriefing'

/** The scope wire form for an ongoing, or 'home'. One speller, so a key cannot drift from a query. */
export function scopeOf(ongoingId) {
  const id = String(ongoingId || '').trim()
  return id ? `ongoing:${id}` : 'home'
}

/** How many files one list asks for. Past this the strip is a file explorer, and there is one. */
export const LIST_SIZE = 100

export const state = {
  // Counts per scope: { [scope]: { status, data } }.
  counts: {},
  // The list currently on screen, and what it is a list OF. One at a time: the strip shows one
  // briefing, so a second list would be a copy of something nobody is looking at.
  list: { scope: null, class: null, status: 'idle', items: [] },
  // The class chip in force, or null for "every attachment".
  className: null,
}

export const getters = {
  /** The counts for a scope — always a record, never undefined, so callers need no guard. */
  counts: s => (scope = 'home') => s.counts[scope] || { status: 'idle', data: null },
  /** Just the numbers, or null while nothing has been fetched — what the briefing builders take. */
  countsData: s => (scope = 'home') => s.counts[scope]?.data || null,
  className: s => s.className,
  list: s => s.list,
  items: s => s.list.items,
  loading: s => s.list.status === 'loading',
}

export const mutations = {
  SET_COUNTS(s, { scope, ...record }) {
    s.counts = { ...s.counts, [scope]: { ...(s.counts[scope] || {}), ...record } }
  },
  SET_LIST(s, record) { s.list = { ...s.list, ...record } },
  SET_CLASS(s, value) { s.className = parseAttachmentClass(value) },
  CLEAR(s) {
    s.list = { scope: null, class: null, status: 'idle', items: [] }
    s.className = null
  },
}

export const actions = {
  /**
   * The counts for a scope, unless we already hold them.
   *
   * Held counts are not re-asked: the row is rebuilt on every strip render, and a fetch per render
   * would be a request per keystroke elsewhere in the box. `force` is for after something changed
   * the files themselves — an upload, a delete.
   */
  async ensureCounts({ commit, state: s, rootGetters }, { scope = 'home', force = false } = {}) {
    // Signed-in only: a guest has no documents, and `documents/briefing` is not a guest endpoint —
    // asking would only bounce the call to the identity provider (2026-08-29). Nothing is held, so
    // the row is simply absent, which is the truth.
    if (!rootGetters['user/isAuthenticated']) return
    const held = s.counts[scope]
    if (!force && held && (held.status === 'ready' || held.status === 'loading')) return

    commit('SET_COUNTS', { scope, status: 'loading' })
    try {
      const res = await api.get('documents/briefing', scope === 'home' ? {} : { scope })
      commit('SET_COUNTS', { scope, status: 'ready', data: unwrap(res) })
    } catch {
      // A failed count is no row at all, not a wrong one: `attachmentsRow(null)` is null, and the
      // strip simply says nothing about attachments — which is true, since we do not know.
      commit('SET_COUNTS', { scope, status: 'error', data: null })
    }
  },

  /** The files themselves, for the scope and the class chip in force. */
  async fetchList({ commit }, { scope = 'home', className = null } = {}) {
    const only = parseAttachmentClass(className)
    commit('SET_LIST', { scope, class: only, status: 'loading', items: [] })
    try {
      const request = { pageSize: LIST_SIZE, page: 1 }
      if (scope !== 'home') request.scope = scope
      if (only) request.class = only
      const res = await api.get('documents', request)
      const page = unwrap(res)
      commit('SET_LIST', { scope, class: only, status: 'ready', items: Array.isArray(page?.items) ? page.items : [] })
    } catch {
      commit('SET_LIST', { scope, class: only, status: 'error', items: [] })
    }
  },

  /**
   * A class chip, clicked. The chip in force turns OFF when clicked again — the same toggle every
   * filter row in the Briefing does, which is why the chips need no "All" chip to escape through
   * (see `nextAttachmentClass`).
   */
  async selectClass({ commit, dispatch, state: s }, { scope = 'home', className = null } = {}) {
    // …through `nextAttachmentClass`, which is what MAKES it a toggle. Storing the clicked class
    // directly would light the chip and leave it lit: the ✕ the active chip draws sends this same
    // action with its own class, so a second click would re-select what was already selected and
    // there would be no way back to "every attachment" short of closing the whole row.
    commit('SET_CLASS', nextAttachmentClass(s.className, className))
    await dispatch('fetchList', { scope, className: s.className })
  },

  /** Opening the entry: the counts (for the chips) and the unfiltered list, together. */
  async open({ commit, dispatch }, { scope = 'home' } = {}) {
    commit('SET_CLASS', null)
    await Promise.all([
      dispatch('ensureCounts', { scope }),
      dispatch('fetchList', { scope, className: null }),
    ])
  },

  /** …and closing it, which forgets the class so the next open starts at "everything". */
  close({ commit }) { commit('CLEAR') },
}

/** The BFF wraps replies as `{ data: { data } }` in some places and `{ data }` in others. */
function unwrap(res) {
  return res?.data?.data ?? res?.data ?? null
}
